import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getHeroList, saveTwGuide, getTwGuideDetail } from '../../api/senaApi';
import { skillImg as senaSkillImg } from './senaAssets';
import './SenaGuideWrite.css';
import './SenaTotalWar.css';
import './SenaTwWrite.css';

const DECK_COUNT = 5;   // 등급 구분 없이 덱 1~5 자유 작성

const FORMATIONS = [
  { id: 'basic', label: '기본진형', front: [1, 2], back: [3, 4, 5] },
  { id: 'balance', label: '밸런스진형', front: [1, 2, 3], back: [4, 5] },
  { id: 'defense', label: '방어진형', front: [1], back: [2, 3, 4, 5] },
  { id: 'protect', label: '보호진형', front: [1, 2, 3, 4], back: [5] },
];

const trySkillImg = (heroNm, skillNo) => senaSkillImg(heroNm, 'ALLY', skillNo);

// 빈 덱 5개
const initDecks = () => Array.from({ length: DECK_COUNT }, (_, i) => ({
  deckOrder: i + 1,
  formation: 'basic',
  description: '',
  heroes: [null, null, null, null, null],
  skills: [null, null, null],
}));

export default function SenaTwWrite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('id'); // 수정 모드
  const userId = localStorage.getItem('userId') || '게스트';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [decks, setDecks] = useState(initDecks);
  const [allyHeroes, setAllyHeroes] = useState([]);
  const [heroSearch, setHeroSearch] = useState('');
  const [activeSlot, setActiveSlot] = useState(null);
  const [skillSlot, setSkillSlot] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getHeroList('ALLY').then(r => {
      const heroes = r.data || [];
      setAllyHeroes(heroes);

      // 수정 모드: 기존 데이터 로드
      if (editId) {
        getTwGuideDetail(Number(editId)).then(res => {
          const guide = res.data;
          if (!guide) return;
          setTitle(guide.title || '');
          setDescription(guide.description || '');

          const loaded = initDecks();
          (guide.decks || []).forEach(deck => {
            const idx = deck.deckOrder - 1;
            if (idx < 0 || idx >= loaded.length) return;

            loaded[idx].formation = deck.formation || 'basic';
            loaded[idx].description = deck.description || '';
            loaded[idx].heroes = [null, null, null, null, null];
            (deck.heroes || []).forEach(dh => {
              const hero = heroes.find(h => h.heroId === dh.heroId);
              if (hero && dh.slotNo >= 1 && dh.slotNo <= 5) {
                loaded[idx].heroes[dh.slotNo - 1] = { ...hero, equipMemo: dh.equipMemo || '' };
              }
            });
            loaded[idx].skills = [null, null, null];
            (deck.skills || []).forEach(ds => {
              if (ds.skillOrder >= 1 && ds.skillOrder <= 3) {
                loaded[idx].skills[ds.skillOrder - 1] = {
                  skillId: ds.skillId, skillNo: ds.skillNo, heroNm: ds.heroNm, skillNm: ds.skillNm,
                };
              }
            });
          });
          setDecks(loaded);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [editId]);

  // 덱 하나만 갈아끼우는 공통 updater
  const updateDeck = (deckIdx, mutate) => {
    setDecks(prev => prev.map((d, i) => (i === deckIdx ? mutate({ ...d }) : d)));
  };

  // 전체 덱에서 사용된 영웅 ID (총력전은 한 영웅을 여러 덱에 못 넣는다)
  const usedHeroIds = () => decks.flatMap(d => d.heroes.filter(Boolean).map(h => h.heroId));

  // 영웅 선택
  const handleSelectHero = (hero) => {
    if (!activeSlot) return;
    const { deckIdx, slotIdx } = activeSlot;

    if (usedHeroIds().includes(hero.heroId)) {
      alert('다른 덱에서 이미 사용된 영웅입니다.');
      return;
    }

    updateDeck(deckIdx, deck => {
      const heroes = [...deck.heroes];
      heroes[slotIdx] = hero;
      deck.heroes = heroes;
      return deck;
    });
    setActiveSlot(null);
    setHeroSearch('');
  };

  const handleRemoveHero = (deckIdx, slotIdx) => {
    updateDeck(deckIdx, deck => {
      const heroes = [...deck.heroes];
      heroes[slotIdx] = null;
      deck.heroes = heroes;
      return deck;
    });
  };

  // 진형 변경
  const handleFormation = (deckIdx, formationId) => {
    updateDeck(deckIdx, deck => { deck.formation = formationId; return deck; });
  };

  // 덱 설명
  const handleDeckDesc = (deckIdx, val) => {
    updateDeck(deckIdx, deck => { deck.description = val; return deck; });
  };

  // 템세팅 메모
  const handleEquipMemo = (deckIdx, slotIdx, val) => {
    updateDeck(deckIdx, deck => {
      const heroes = [...deck.heroes];
      if (heroes[slotIdx]) heroes[slotIdx] = { ...heroes[slotIdx], equipMemo: val };
      deck.heroes = heroes;
      return deck;
    });
  };

  // 예약스킬 선택
  const handleSelectSkill = (skill) => {
    if (!skillSlot) return;
    const { deckIdx, skillIdx } = skillSlot;

    // 같은 덱 내 스킬 중복 체크
    const currentSkills = decks[deckIdx]?.skills || [];
    if (currentSkills.some(s => s && s.skillId === skill.skillId)) {
      alert('이미 예약된 스킬입니다.');
      return;
    }

    updateDeck(deckIdx, deck => {
      const skills = [...deck.skills];
      skills[skillIdx] = skill;
      deck.skills = skills;
      return deck;
    });
    setSkillSlot(null);
  };

  const handleRemoveSkill = (deckIdx, skillIdx) => {
    updateDeck(deckIdx, deck => {
      const skills = [...deck.skills];
      skills[skillIdx] = null;
      deck.skills = skills;
      return deck;
    });
  };

  // 현재 덱의 영웅 스킬 목록
  const getDeckSkills = (deckIdx) => {
    const deck = decks[deckIdx];
    if (!deck) return [];
    return deck.heroes.filter(Boolean).flatMap(h =>
      (h.skills || []).map(sk => ({ ...sk, heroNm: h.heroNm }))
    );
  };

  // 저장
  const handleSave = async () => {
    if (!title.trim()) { alert('제목을 입력해주세요.'); return; }

    // 영웅이 하나도 없는 덱은 저장하지 않는다 (덱 번호는 화면 순서 유지)
    const allDecks = decks
      .filter(deck => deck.heroes.some(Boolean))
      .map(deck => ({
        deckOrder: deck.deckOrder,
        formation: deck.formation,
        description: deck.description || null,
        heroes: deck.heroes.filter(Boolean).map((h, i) => ({
          heroId: h.heroId,
          slotNo: i + 1,
          equipMemo: h.equipMemo || null,
        })),
        skills: deck.skills.filter(Boolean).map((sk, i) => ({
          skillId: sk.skillId,
          skillOrder: i + 1,
        })),
      }));

    if (allDecks.length === 0) { alert('최소 1개 덱을 구성해주세요.'); return; }

    setSaving(true);
    try {
      await saveTwGuide({
        twGuideId: editId ? Number(editId) : undefined,
        title, authorNm: userId,
        description: description || null,
        decks: allDecks,
      });
      alert('총력전 공략이 저장되었습니다.');
      navigate('/sena/total-war');
    } catch (e) {
      alert(e?.response?.data || '저장 실패');
    } finally { setSaving(false); }
  };

  const used = usedHeroIds();

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate(-1)}>← 뒤로</button>
        <button className="siege-home-btn" onClick={() => navigate('/sena')}>홈</button>
        <h1 className="sena-header-guild">총력전 작성</h1>
      </header>

      <div className="tw-container">
        {/* 기본 정보 */}
        <section className="gw-section">
          <h3 className="gw-section-title">기본 정보</h3>
          <div className="gw-form-row">
            <label className="gw-label">제목</label>
            <input className="gw-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="공략 제목" />
          </div>
          <div className="gw-form-row">
            <label className="gw-label">작성자</label>
            <input className="gw-input gw-input-sm" value={localStorage.getItem('userName') || userId} disabled />
          </div>
          <textarea className="gw-textarea" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="전체 설명 (선택)" rows={3} />
        </section>

        {/* 덱 편집 — 덱 1~5, 채운 덱만 저장된다 */}
        {decks.map((deck, deckIdx) => {
          const fm = FORMATIONS.find(f => f.id === deck.formation);
          return (
            <section key={deckIdx} className="gw-section tw-deck-edit">
              <h3 className="gw-section-title">덱 {deck.deckOrder}</h3>

              {/* 진형 */}
              <div className="gw-formation-group" style={{ marginBottom: 12 }}>
                {FORMATIONS.map(f => (
                  <button key={f.id} className={`gw-formation-btn ${deck.formation === f.id ? 'active' : ''}`}
                    onClick={() => handleFormation(deckIdx, f.id)}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* 영웅 슬롯 */}
              <div className="gw-ally-slots">
                {deck.heroes.map((hero, slotIdx) => {
                  const isFront = fm?.front.includes(slotIdx + 1);
                  const isActive = activeSlot?.deckIdx === deckIdx && activeSlot?.slotIdx === slotIdx;
                  return (
                    <div key={slotIdx} className={`gw-ally-slot ${hero ? 'filled' : ''} ${isActive ? 'selecting' : ''}`}
                      onClick={() => !hero && setActiveSlot({ deckIdx, slotIdx })}>
                      <span className={`gw-slot-label ${isFront ? 'front' : 'back'}`}>
                        {isFront ? '전열' : '후열'}
                      </span>
                      {hero ? (
                        <>
                          <span className="gw-ally-name">{hero.heroNm}</span>
                          <button className="gw-ally-remove" onClick={(e) => { e.stopPropagation(); handleRemoveHero(deckIdx, slotIdx); }}>×</button>
                        </>
                      ) : (
                        <span className="gw-ally-empty">{isActive ? '선택 중...' : `슬롯 ${slotIdx + 1}`}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 영웅 검색 (활성 슬롯이 이 덱일 때) */}
              {activeSlot?.deckIdx === deckIdx && (
                <div className="gw-hero-search-wrap" style={{ marginBottom: 10 }}>
                  <input className="gw-hero-search" placeholder="영웅 검색..."
                    value={heroSearch} onChange={e => setHeroSearch(e.target.value)} autoFocus />
                  {heroSearch.length > 0 && (
                    <div className="gw-hero-dropdown">
                      {allyHeroes
                        .filter(h => h.heroNm.includes(heroSearch))
                        .filter(h => !used.includes(h.heroId))
                        .map(h => (
                          <button key={h.heroId} className="gw-hero-dropdown-item" onClick={() => handleSelectHero(h)}>
                            {h.heroNm}
                          </button>
                        ))}
                      {allyHeroes.filter(h => h.heroNm.includes(heroSearch)).filter(h => !used.includes(h.heroId)).length === 0 && (
                        <div className="gw-hero-dropdown-empty">검색 결과 없음 (다른 덱에 배치된 영웅 제외)</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 템세팅 메모 */}
              {deck.heroes.some(Boolean) && (
                <div className="tw-equip-section">
                  <p className="gw-hint">영웅별 템세팅:</p>
                  <div className="tw-equip-list">
                    {deck.heroes.filter(Boolean).map((hero, i) => {
                      const origIdx = deck.heroes.indexOf(hero);
                      return (
                        <div key={i} className="tw-equip-row">
                          <span className="tw-equip-name">{hero.heroNm}</span>
                          <input className="tw-equip-input" placeholder="템세팅 메모"
                            value={hero.equipMemo || ''}
                            onChange={e => handleEquipMemo(deckIdx, origIdx, e.target.value)} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 예약스킬 3개 */}
              <div className="tw-skill-section">
                <p className="gw-hint">예약스킬 (3개):</p>
                <div className="tw-skill-slots">
                  {deck.skills.map((sk, skillIdx) => {
                    const isActive = skillSlot?.deckIdx === deckIdx && skillSlot?.skillIdx === skillIdx;
                    return (
                      <div key={skillIdx} className={`tw-skill-slot ${sk ? 'filled' : ''} ${isActive ? 'selecting' : ''}`}
                        onClick={() => !sk && setSkillSlot({ deckIdx, skillIdx })}>
                        {sk ? (
                          <>
                            {trySkillImg(sk.heroNm, sk.skillNo)
                              ? <img className="tw-skill-img" src={trySkillImg(sk.heroNm, sk.skillNo)} alt="" />
                              : <span className="tw-skill-text">{sk.heroNm} 스킬{sk.skillNo}</span>}
                            <button className="tw-skill-remove" onClick={(e) => { e.stopPropagation(); handleRemoveSkill(deckIdx, skillIdx); }}>×</button>
                          </>
                        ) : (
                          <span className="tw-skill-empty">{isActive ? '선택 중' : `스킬 ${skillIdx + 1}`}</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* 스킬 선택 팔레트 */}
                {skillSlot?.deckIdx === deckIdx && (
                  <div className="tw-skill-palette">
                    {getDeckSkills(deckIdx).map(sk => {
                      const img = trySkillImg(sk.heroNm, sk.skillNo);
                      return (
                        <button key={sk.skillId} className="gw-skill-btn-img ally"
                          onClick={() => handleSelectSkill(sk)}>
                          {img ? <img src={img} alt="" className="gw-skill-btn-icon" />
                            : <span>{sk.heroNm} 스킬{sk.skillNo}</span>}
                        </button>
                      );
                    })}
                    {getDeckSkills(deckIdx).length === 0 && (
                      <p className="gw-hint">영웅을 먼저 배치하세요.</p>
                    )}
                  </div>
                )}
              </div>

              {/* 덱 설명 */}
              <input className="gw-input" placeholder="덱 설명 (선택)"
                value={deck.description} onChange={e => handleDeckDesc(deckIdx, e.target.value)} />
            </section>
          );
        })}

        {/* 저장 */}
        <div className="gw-footer">
          <button className="gw-btn" onClick={() => navigate(-1)}>취소</button>
          <button className="gw-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
